import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { IdObject } from "@app/_classes/cosmos-entity";
import {
	hasPermissionByKey,
	hasUserRoleControlling,
	hasUserRoleManager,
	isAdmin,
	isProjectManagerOfUser,
	isSupervisorOfUser,
} from "@app/_helpers/permission";
import { createRxValue, fromRxValue, userIsAwaitingActivation } from "@app/_helpers/utils";
import { ComeAndGoAbsenceDialogComponent } from "@app/components/come-and-go-absence-dialog/come-and-go-absence-dialog.component";
import { ComeAndGoCreateDialogComponent } from "@app/components/come-and-go-create-dialog/come-and-go-create-dialog.component";
import { resolveAbsenceCreatePermissions } from "@app/components/come-and-go-create-dialog/come-and-go-utils";
import { InsightsPageComponent } from "@app/pages/insights-page/insights-page.component";
import { permissionKindFromTab, ReportsService, ReportsTab } from "@app/pages/reports-page/reports.service";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UserSettingsQuery } from "@timeghost/web-api";
import { xor } from "lodash-es";
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, of, skip, startWith } from "rxjs";
import { firstBy } from "thenby";
@UntilDestroy()
@Component({
	selector: "tg-employee-filter-popover",
	templateUrl: "./employee-filter-popover.component.html",
	styleUrls: ["./employee-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class EmployeeFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef,
		private router: Router,
		@Optional() public reportsService?: ReportsService,
		@Optional() private insightsPage?: InsightsPageComponent,
		@Optional() private comeAndGoCreateDialog?: ComeAndGoCreateDialogComponent,
		@Optional() private comeAndGoAbsenceDialog?: ComeAndGoAbsenceDialogComponent
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}

	/** Route/host context for permission filtering in items$filtered (factory runs after construction). */
	private filterPageKey$(): Observable<string | null> {
		if (this.reportsService) {
			return this.reportsService.reportsTab.asObservable().pipe(
				map((tab) => (tab ? permissionKindFromTab(tab as ReportsTab) : null)),
				startWith(
					this.reportsService.reportsTab.value
						? permissionKindFromTab(this.reportsService.reportsTab.value as ReportsTab)
						: null
				),
				distinctUntilChanged()
			);
		}
		if (this.insightsPage) {
			return of("insights");
		}
		if (this.comeAndGoCreateDialog) {
			return of("workinghoursDialog");
		}
		if (this.comeAndGoAbsenceDialog) {
			return of("absenceDialog");
		}
		return of(null);
	}
	search = new FormControl("", [Validators.minLength(2)]);
	private _selectedItems = new BehaviorSubject<string[]>([]);
	readonly selectedItems$ = this._selectedItems.asObservable().pipe(distinctUntilChanged());
	private _excludeIds = new BehaviorSubject<string[]>([]);
	@Input()
	set excludeIds(val: string[]) {
		this._excludeIds.next(val ?? []);
	}
	showRemovedUsers = createRxValue(false);
	get selectedItems() {
		return this._selectedItems.getValue();
	}
	@Input()
	set selectedItems(val: string[]) {
		const prev = this.selectedItems;
		this._selectedItems.next(val);

		if (xor(val, prev).length > 0) this.selectedItemsChange.emit(val);
	}
	@Output()
	selectedItemsChange = new EventEmitter(true);
	readonly items$filtered = fromRxValue(() =>
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.getRawValue()), distinctUntilChanged()),
			this.userSettingsQuery.select(),
			this.filterPageKey$(),
			this.selectedItems$,
			this.showRemovedUsers.asObservable(),
			this._excludeIds.asObservable(),
		]).pipe(
			map(([q, user, currentPage, selected, showRemovedUsers, excludeIds]) => {
				const userIsAdmin = isAdmin(user);
				const userManager = hasUserRoleManager(user);
				const isControlling = hasUserRoleControlling(user);
				const userProjectManagerOfUser = isProjectManagerOfUser(user);
				const comegoEnabled = user.workspace.settings?.workinghoursEnabled ?? true;
				const canEditAllTimes = hasPermissionByKey(user, "groupsCanComegoViewTimes");
				const canViewPublicTimes = hasPermissionByKey(user, "groupsCanSeePublicTimes");
				const canViewPrivateTimes = hasPermissionByKey(user, "groupsCanSeePrivateTimes");
				const users = user.workspace.users ?? [];
				return (
					this.search.invalid
						? users
						: users.filter((x) => {
								return x.name.toLowerCase().indexOf(q.toLowerCase()) !== -1;
							})
				)
					.map((x) => {
						return {
							...x,
							selected: selected.findIndex((u) => u === x.id) !== -1,
							subordinate: isSupervisorOfUser(user, x.id),
							currentUser: x.id === user.id,
						};
					})
					.filter((d) => {
						if (!showRemovedUsers && d.removed) return false;
						return true;
					})
					.filter((d) => {
						if (userIsAdmin) return d;
						else if (currentPage === "insights") return d;
						else if (currentPage === "workinghoursDialog")
							return d.id === user.id || userManager || d.subordinate;
						else if (currentPage === "project")
							return (
								d.id === user.id ||
								userIsAdmin ||
								userManager ||
								isControlling ||
								userProjectManagerOfUser(d.id) ||
								canViewPublicTimes ||
								canViewPrivateTimes
							);
						else if (currentPage === "workinghours")
							return (
								d.id === user.id ||
								userIsAdmin ||
								userManager ||
								d.subordinate ||
								(!comegoEnabled && isControlling)
							);
						else if (currentPage === "absenceDialog") {
							// if user is admin or manager, show all
							if (userIsAdmin || userManager) return d;
							// check for self if user can create absence for self
							else if (d.id === user.id) {
								return resolveAbsenceCreatePermissions(user, d.id);
							}
							return d.subordinate;
						} else return d.id === user.id || userIsAdmin || userManager || d.subordinate;
					})
					.filter((d) => {
						if (!showRemovedUsers && d.removed) return false;
						if (!d.has_license || userIsAwaitingActivation(d.id)) return false;
						return true;
					})
					.filter((d) => !excludeIds.includes(d.id))
					.sort(
						firstBy("selected", "desc")
							.thenBy("removed", "asc")
							.thenBy("subordinate", "desc")
							.thenBy("name", "asc")
					);
			})
		)
	);
	readonly currentUserItem$filtered = fromRxValue(() =>
		this.items$filtered.asObservable().pipe(map((items) => items.find((item) => item.currentUser) ?? null))
	);
	readonly otherItems$filtered = fromRxValue(() =>
		this.items$filtered.asObservable().pipe(map((items) => items.filter((item) => !item.currentUser)))
	);

	@Input() hideSubordinateIcon = false;

	@Output()
	itemsClose = new EventEmitter(true);
	@Output()
	toggleRemoved = new EventEmitter(true);
	ngOnInit(): void {
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
		this.closed.asObservable().subscribe(() => this.toggleRemoved.emit(this.showRemovedUsers.value));
		// Skip initial emit so empty selectedItems before input bind cannot wipe teamFilter.
		this.showRemovedUsers
			.asObservable()
			.pipe(skip(1), untilDestroyed(this))
			.subscribe(() => this.filterRemovedUsers());
	}
	trackId(index: number, { id }: { id: string }) {
		return id;
	}
	selectItem(id: string) {
		if (this.selectedItems.findIndex((x) => x === id) !== -1)
			this.selectedItems = this.selectedItems.filter((x) => x !== id);
		else this.selectedItems = [...this.selectedItems, id];
	}
	selectAll() {
		this.selectedItems = this.items$filtered.value.map((employee) => employee.id).filter((id) => !!id);
	}
	selectSubordinates() {
		const user = this.userSettingsQuery.getValue();
		this.selectedItems = this.items$filtered.value
			.map((employee) => employee.id)
			.filter(
				(id) =>
					!!id &&
					user.workspace.users?.find(
						(d) => d.id === id && d["supervisors"]?.find((u: IdObject) => u.id === user.id)
					)
			);
	}

	selectNone() {
		this.selectedItems = [];
	}
	close() {
		super.close(this.selectedItems);
	}

	filterRemovedUsers() {
		const workspaceUser = this.userSettingsQuery.getValue().workspace.users;
		this.reportsService?.teamFilter.update(
			this.selectedItems.filter((userId) => {
				if (!this.showRemovedUsers.value) {
					return !workspaceUser.find((u) => u.id == userId).removed;
				} else return true;
			})
		);
	}
}
